import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchAdminStats, fetchIntegrationStatus, api } from "../lib/api";
import Logo from "../components/Logo";
import IntegrationsHealthPanel from "../components/IntegrationsHealthPanel";
import HermesPanel from "../components/HermesPanel";

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [integrations, setIntegrations] = useState(null);

  useEffect(() => {
    fetchAdminStats().then(setStats).catch(() => {});
    fetchIntegrationStatus().then(setIntegrations).catch(() => {});
  }, []);

  if (!stats) {
    return <div style={{ padding: 40 }} data-testid="admin-loading">Loading…</div>;
  }

  return (
    <div data-testid="admin-dashboard" style={{ padding: "32px 20px 80px", maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 4 }}>
        <Logo variant="dark" size={44} testId="admin-logo" />
        <span style={{ color: "#D4AF37", letterSpacing: 4, fontFamily: "Bebas Neue, sans-serif", fontSize: 16 }}>
          · ADMIN · LANRE COMMAND CENTER
        </span>
      </div>
      <h1 className="font-display" style={{ fontSize: 56, color: "#0A1628", margin: "8px 0 6px" }}>
        NEARNOW HQ
      </h1>
      <p style={{ color: "#5a5d66", fontSize: 17 }}>
        Foundation MVP · Every integration showing <span className="mock-badge">MOCK</span> is wired but using simulated data until you set its env var.
      </p>

      {/* KPI Strip */}
      <div data-testid="admin-kpi-strip" style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 14, marginTop: 22,
      }}>
        <Kpi label="MRR" value={`$${(stats.mrr_dollars).toLocaleString()}`} />
        <Kpi label="Contractors" value={stats.contractors_total} />
        <Kpi label="Paying" value={stats.contractors_paying} />
        <Kpi label="Founding Active" value={`${stats.founding_active} / 100`} />
        <Kpi label="Inbox Unread" value={stats.inbox_unread} highlight={stats.inbox_unread > 0} />
        <Kpi label="Consumer Emails" value={stats.consumer_emails_captured} />
        <Kpi label="ARIA Token Cap" value={stats.aria_token_cap.toLocaleString()} sub={stats.mrr_cents < 3000000 ? "(< $30K MRR)" : "(≥ $30K MRR)"} />
      </div>

      {/* Quick links */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14, marginTop: 22 }}>
        <PanelLink to="/admin/inbox" title="Inbox" detail={`${stats.inbox_unread} unread · ${stats.inbox_total} total · contact@nearnow.biz`} />
        <PanelLink to="/admin/launch-readiness" title="Launch Readiness" detail="Every green light needed before ad spend · Retell · SendGrid · Meta · Stripe · Telnyx" badge="LIVE" />
        <PanelLink to="/admin/ad-preview" title="Meta Ad Preview" detail="See the exact ad creative + headline + placements before spending a dollar" badge="LIVE" />
        <PanelLink to="/admin/activations" title="Activation Queue" detail="New paid signups awaiting call-forwarding setup · call within 2 hours" badge="LIVE" />
        <PanelLink to="/admin/analytics" title="Account Analytics" detail="6 metrics: forwarding %, calls answered, answer rate, qualified, $ captured, COGS" badge="LIVE" />
        <RetellPurchaseCard />
        <PanelLink to="/admin/directory" title="RI Contractor Directory" detail="Import from Google · manage NearNow-verified partners · pinned on /find-a-pro" badge="LIVE" />
        <PanelLink to="/admin/marketing" title="Marketing · Review Wins" detail="Curate review milestones · public /testimonials wall · social-media copy" badge="LIVE" />
        <PanelLink to="/admin/acos" title="ACOS A/B Testing" detail="Pricing · Onboarding · Demo scripts · Ad copy" badge="MOCK" />
        <PanelLink to="/admin/seo" title="SEO + AEO" detail="Programmatic city pages + Coming Soon waitlist" badge="MOCK" />
      </div>

      {/* Gates */}
      <div data-testid="admin-gates" style={{ marginTop: 26, padding: 22, background: "#0A1628", color: "#fff", borderRadius: 10 }}>
        <h2 className="font-display" style={{ fontSize: 30, color: "#D4AF37", margin: "0 0 14px" }}>RI DOMINATION GATES</h2>
        <Gate label="Consumer ad gate" unlocked={stats.consumer_ad_gate_unlocked} detail={`Unlocks at ${stats.consumer_ad_gate_unlocked ? "✓ unlocked" : "50 active contractors"}`} />
        <Gate label="MA expansion" unlocked={stats.ma_unlocked} detail="Unlocks at 200 contractors + $50K MRR" />
        <Gate label="Elite tier (public)" unlocked={false} detail="Hidden from public until Month 6 — upsell-only via Agent 12" />
        <Gate label="Market Leader tier (public)" unlocked={false} detail="Hidden from public until Month 6 — upsell-only via Agent 12" />
      </div>

      {/* Integrations */}
      <div data-testid="admin-integrations" style={{ marginTop: 26 }}>
        <h2 className="font-display" style={{ fontSize: 30, color: "#0A1628", margin: "0 0 14px" }}>INTEGRATIONS</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 10 }}>
          {integrations && Object.entries(integrations.integrations).map(([key, v]) => (
            <div key={key} data-testid={`integration-${key}`} style={{
              background: "#fff", padding: "14px 16px", borderRadius: 8,
              border: "1px solid #e5e7ee", display: "flex", justifyContent: "space-between", alignItems: "flex-start",
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 20, letterSpacing: 1, color: "#0A1628" }}>
                  {v.label}
                </div>
                <div style={{ fontSize: 13, color: "#8a8d96", marginTop: 2 }}>
                  env: <code>{v.env_var}</code>
                </div>
                {v.missing && v.missing.length > 0 && (
                  <div style={{ fontSize: 12, color: "#8B4500", marginTop: 4 }}>
                    Still need: {v.missing.join(", ")}
                  </div>
                )}
              </div>
              {v.status === "live" ? (
                <span className="live-badge">LIVE</span>
              ) : v.status === "partial" ? (
                <span className="partial-badge">PARTIAL</span>
              ) : (
                <span className="mock-badge">MOCK</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Hermes — executive brain */}
      <HermesPanel />

      {/* Integration Health (failure audit) */}
      <IntegrationsHealthPanel />

      {/* Roadmap reminder */}
      <div style={{ marginTop: 36, padding: 22, background: "#FFF8DC", border: "1px dashed #D4AF37", borderRadius: 8 }}>
        <h3 className="font-display" style={{ fontSize: 26, color: "#0A1628", margin: 0 }}>NEXT IN THE BUILD</h3>
        <ul style={{ marginTop: 10, fontSize: 16, color: "#1A1A2E" }}>
          <li>16 agents + ACOS meta-agent + ARIA nightly cycle</li>
          <li>RUFLO v3.6.12 hive-mind routing</li>
          <li>Thomas sales dashboard with live scarcity panel</li>
          <li>Programmatic /[vertical]/[city] SEO pages + AEO answer pages</li>
        </ul>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, sub, highlight }) => (
  <div style={{
    background: "#fff", padding: "16px 18px", borderRadius: 8, border: "1px solid #e5e7ee",
    borderTop: highlight ? "4px solid #A32D2D" : "4px solid #D4AF37",
  }}>
    <div style={{ fontFamily: "Bebas Neue, sans-serif", fontSize: 52, color: "#0A1628", lineHeight: 1 }}>
      {value}
    </div>
    <div style={{ fontSize: 14, color: "#5a5d66", letterSpacing: 1, marginTop: 4, textTransform: "uppercase" }}>
      {label}
    </div>
    {sub && <div style={{ fontSize: 12, color: "#8a8d96", marginTop: 2 }}>{sub}</div>}
  </div>
);

const PanelLink = ({ to, title, detail, badge }) => (  <Link to={to} data-testid={`panel-${to.replace(/[^a-z]/g, "")}`}
    style={{ textDecoration: "none", background: "#fff", border: "1px solid #e5e7ee", borderRadius: 8, padding: 20, display: "block" }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <div className="font-display" style={{ fontSize: 26, color: "#0A1628", letterSpacing: 1 }}>{title}</div>
      {badge && <span className="mock-badge">{badge}</span>}
    </div>
    <div style={{ fontSize: 15, color: "#5a5d66", marginTop: 6 }}>{detail}</div>
    <div style={{ fontSize: 14, color: "#D4AF37", marginTop: 10, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2 }}>OPEN →</div>
  </Link>
);

const Gate = ({ label, unlocked, detail }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #1a2640" }}>
    <div>
      <div style={{ fontSize: 17 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#8a8d96" }}>{detail}</div>
    </div>
    <span style={{
      padding: "4px 10px", borderRadius: 4, fontSize: 12, fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
      background: unlocked ? "#1A7A4A" : "#A32D2D", color: "#fff",
    }}>
      {unlocked ? "UNLOCKED" : "LOCKED"}
    </span>
  </div>
);

export default AdminDashboard;

const RetellPurchaseCard = () => {
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState(null);
  const buy = async () => {
    if (!window.confirm("Attempt to buy a Retell 401 phone number for ARIA? Requires a payment method on your Retell dashboard.")) return;
    setStatus("busy"); setMsg(null);
    try {
      const r = await api.post("/admin/retell/purchase-number").then((x) => x.data);
      setStatus("ok");
      setMsg(`Purchased ${r.phone_number}. ${r.next_step}`);
    } catch (e) {
      setStatus("err");
      setMsg(e?.response?.data?.detail || e?.message || "Purchase failed.");
    }
  };
  return (
    <div data-testid="retell-purchase-card"
         style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="font-display" style={{ fontSize: 26, color: "#0A1628", letterSpacing: 1 }}>Provision Retell Number</div>
        <span className="mock-badge">SETUP</span>
      </div>
      <div style={{ fontSize: 15, color: "#5a5d66", marginTop: 6 }}>
        Buy a 401 line for ARIA in one tap. Requires a payment method on your Retell dashboard first.
      </div>
      <button onClick={buy} disabled={status === "busy"}
              data-testid="retell-purchase-btn"
              style={{ marginTop: 12, background: "#D4AF37", color: "#0A1628", border: "none",
                        padding: "10px 16px", borderRadius: 8, fontWeight: 700, letterSpacing: 1,
                        fontSize: 12, fontFamily: "Bebas Neue, sans-serif", cursor: "pointer" }}>
        {status === "busy" ? "PURCHASING…" : "BUY 401 NUMBER →"}
      </button>
      {msg && (
        <div data-testid="retell-purchase-msg" style={{
          marginTop: 10, fontSize: 13, padding: "8px 12px", borderRadius: 8,
          background: status === "ok" ? "#E7F5EE" : "#FDECEC",
          color: status === "ok" ? "#0F5132" : "#A32D2D",
        }}>{msg}</div>
      )}
    </div>
  );
};
